import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  signInUser,
  resetPassword,
  signInWithGoogle,
  completeGoogleRedirect,
  authErrorMessage,
} from "../firebase";
import AuthLayout from "./AuthLayout";
import GoogleButton from "./GoogleButton";

export default function Login() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Si se volvió de la redirección de Google, completar el ingreso
  useEffect(() => {
    completeGoogleRedirect()
      .then((user) => user && navigate("/home"))
      .catch((err) => setError(authErrorMessage(err)));
  }, [navigate]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!email || !password) {
      setError("Ingresá tu email y tu contraseña.");
      return;
    }
    setError("");
    setInfo("");
    setLoading(true);
    try {
      await signInUser(email.trim(), password);
      navigate("/home");
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setInfo("");
    setGoogleLoading(true);
    try {
      const user = await signInWithGoogle();
      if (user) navigate("/home");
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    if (!email) {
      setError("Escribí tu email y tocá de nuevo «¿Olvidaste tu contraseña?».");
      return;
    }
    setError("");
    try {
      await resetPassword(email.trim());
    } catch {
      // No se revela si el email tiene cuenta
    }
    setInfo(`Si existe una cuenta con ${email.trim()}, te enviamos un enlace para crear una contraseña nueva.`);
  };

  return (
    <AuthLayout title="Iniciar sesión" subtitle="Bienvenido a Caro">
      <Stack spacing={2} sx={{ mt: 3 }}>
        <GoogleButton onClick={handleGoogle} loading={googleLoading} />

        <Divider sx={{ color: "text.secondary", fontSize: 13 }}>o con tu email</Divider>

        {error && <Alert severity="error">{error}</Alert>}
        {info && <Alert severity="success">{info}</Alert>}

        <Box component="form" onSubmit={onSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Link href="#" onClick={handleForgotPassword} variant="body2" sx={{ alignSelf: "flex-end" }}>
              ¿Olvidaste tu contraseña?
            </Link>
            <Button variant="contained" color="secondary" type="submit" size="large" fullWidth disabled={loading}>
              {loading ? "Ingresando..." : "Iniciar sesión"}
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" sx={{ textAlign: "center", pt: 1 }}>
          ¿No tenés cuenta?{" "}
          <Link component={RouterLink} to="/register" sx={{ fontWeight: 600 }}>
            Crear una cuenta
          </Link>
        </Typography>
      </Stack>
    </AuthLayout>
  );
}
